import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Calendar, Clock, Users, Radio, Plus, ChevronRight, Bell, BellOff,
  MapPin, Tag, X, ArrowLeft, Loader2, Sparkles
} from 'lucide-react';
import UserAvatar from '../components/UserAvatar';

const CATEGORIES = [
  { value: 'general', label: 'General', color: 'bg-muted text-foreground' },
  { value: 'music', label: 'Music Session', color: 'bg-violet-500/20 text-violet-400' },
  { value: 'podcast', label: 'Podcast', color: 'bg-blue-500/20 text-blue-400' },
  { value: 'battle', label: 'Battle', color: 'bg-red-500/20 text-red-400' },
  { value: 'collab', label: 'Collab', color: 'bg-green-500/20 text-green-400' },
  { value: 'qa', label: 'Q&A', color: 'bg-amber-500/20 text-amber-400' },
  { value: 'listening_party', label: 'Listening Party', color: 'bg-pink-500/20 text-pink-400' },
  { value: 'interview', label: 'Interview', color: 'bg-cyan-500/20 text-cyan-400' },
];

function formatCountdown(scheduledAt) {
  const diff = new Date(scheduledAt) - new Date();
  if (diff <= 0) return 'Starting now';
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

function formatEventDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatEventTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function EventCard({ event, user, onRsvp }) {
  const navigate = useNavigate();
  const isHost = user?.user_id === event.host_user_id;
  const isRsvpd = event.rsvp_user_ids?.includes(user?.user_id);
  const isPast = new Date(event.scheduled_at) < new Date();
  const cat = CATEGORIES.find(c => c.value === event.category) || CATEGORIES[0];

  return (
    <Card
      className="border border-border/50 bg-card/80 hover:border-primary/30 transition-all cursor-pointer overflow-hidden group"
      onClick={() => navigate(`/events/${event.event_id}`)}
      data-testid={`event-card-${event.event_id}`}
    >
      {/* Cover or gradient */}
      <div className="h-28 relative overflow-hidden">
        {event.cover_image_url ? (
          <img src={event.cover_image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/30 via-purple-600/20 to-pink-600/20" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
        <div className="absolute top-3 left-3 flex gap-2">
          <Badge className={`${cat.color} text-xs font-medium`}>{cat.label}</Badge>
          {isPast && <Badge variant="secondary" className="text-xs">Past</Badge>}
        </div>
        {!isPast && (
          <div className="absolute top-3 right-3">
            <Badge className="bg-primary/90 text-white text-xs font-mono tracking-wider">
              <Clock className="w-3 h-3 mr-1" />
              {formatCountdown(event.scheduled_at)}
            </Badge>
          </div>
        )}
      </div>

      <div className="p-4">
        <h3 className="font-bold text-foreground text-lg line-clamp-1 mb-1">{event.title}</h3>
        {event.description && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{event.description}</p>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Calendar className="w-3.5 h-3.5" />
            <span>{formatEventDate(event.scheduled_at)}</span>
            <span className="text-muted-foreground/50">|</span>
            <Clock className="w-3.5 h-3.5" />
            <span>{formatEventTime(event.scheduled_at)}</span>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Users className="w-3.5 h-3.5" />
            <span>{event.rsvp_count || 0}</span>
          </div>
        </div>

        <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/30">
          <div className="flex items-center gap-2">
            <UserAvatar
              src={event.host_avatar}
              alt={event.host_name}
              size="xs"
              userId={event.host_user_id}
              userName={event.host_name}
            />
            <span className="text-xs text-muted-foreground">
              {isHost ? 'Your event' : event.host_name}
            </span>
          </div>
          {!isHost && !isPast && (
            <Button
              size="sm"
              variant={isRsvpd ? 'secondary' : 'default'}
              className="text-xs h-7 px-3"
              onClick={(e) => { e.stopPropagation(); onRsvp(event.event_id); }}
              data-testid={`rsvp-btn-${event.event_id}`}
            >
              {isRsvpd ? <><BellOff className="w-3 h-3 mr-1" /> Cancel</> : <><Bell className="w-3 h-3 mr-1" /> Remind Me</>}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

function CreateEventForm({ onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('general');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [loading, setLoading] = useState(false);
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !date || !time) {
      toast.error('Title, date, and time are required');
      return;
    }
    setLoading(true);
    try {
      const scheduledAt = new Date(`${date}T${time}`).toISOString();
      const res = await fetch(`${API}/scheduled-events/create`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          scheduled_at: scheduledAt,
          category,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Event scheduled!');
        onCreated?.(data);
        onClose();
      } else {
        toast.error(data.detail || 'Failed to create event');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  };

  // Default date to tomorrow
  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setDate(tomorrow.toISOString().split('T')[0]);
    setTime('19:00');
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <Card className="w-full max-w-lg bg-card border-border/50 p-6" onClick={e => e.stopPropagation()} data-testid="create-event-modal">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-foreground">Schedule a Live</h2>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="w-5 h-5" /></Button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground mb-1 block">Title</label>
            <Input
              placeholder="What's your live about?"
              value={title}
              onChange={e => setTitle(e.target.value)}
              maxLength={100}
              data-testid="event-title-input"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground mb-1 block">Description</label>
            <Textarea
              placeholder="Tell people what to expect..."
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              maxLength={500}
              data-testid="event-description-input"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Category</label>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map(c => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setCategory(c.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    category === c.value
                      ? 'bg-primary text-white ring-2 ring-primary/50'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Date</label>
              <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="event-date-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Time</label>
              <Input type="time" value={time} onChange={e => setTime(e.target.value)} data-testid="event-time-input" />
            </div>
          </div>
          <Button type="submit" className="w-full" disabled={loading} data-testid="create-event-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Radio className="w-4 h-4 mr-2" />}
            Schedule Live Event
          </Button>
        </form>
      </Card>
    </div>
  );
}

export default function ScheduledEventsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
  const [events, setEvents] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [filterCat, setFilterCat] = useState(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const url = filterCat
        ? `${API}/scheduled-events/upcoming?limit=30&category=${filterCat}`
        : `${API}/scheduled-events/upcoming?limit=30`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setEvents(data.events || []);
      }
    } catch { /* ignore */ }

    if (user) {
      try {
        const res = await fetch(`${API}/scheduled-events/my-events`, { headers: getAuthHeaders() });
        if (res.ok) {
          const data = await res.json();
          setMyEvents(data.events || []);
        }
      } catch { /* ignore */ }
    }
    setLoading(false);
  }, [user, filterCat]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const handleRsvp = async (eventId) => {
    if (!user) { toast.error('Please log in to RSVP'); return; }
    try {
      const res = await fetch(`${API}/scheduled-events/${eventId}/rsvp`, {
        method: 'POST', headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        fetchEvents();
      } else {
        toast.error(data.detail || 'Failed to RSVP');
      }
    } catch { toast.error('Network error'); }
  };

  const tabs = [
    { key: 'upcoming', label: 'Upcoming' },
    { key: 'my-events', label: 'My Events' },
  ];

  const displayEvents = activeTab === 'my-events' ? myEvents : events;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground tracking-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                Scheduled Lives
              </h1>
              <p className="text-sm text-muted-foreground">Upcoming live sessions from creators</p>
            </div>
          </div>
          {user && (
            <Button onClick={() => setShowCreate(true)} data-testid="schedule-event-btn">
              <Plus className="w-4 h-4 mr-2" /> Schedule Live
            </Button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4 border-b border-border/30">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === t.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`tab-${t.key}`}
            >
              {t.label}
              {t.key === 'my-events' && myEvents.length > 0 && (
                <span className="ml-1.5 text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded-full">{myEvents.length}</span>
              )}
            </button>
          ))}
        </div>

        {/* Category filters */}
        {activeTab === 'upcoming' && (
          <div className="flex gap-2 overflow-x-auto pb-3 mb-4 scrollbar-hide">
            <button
              onClick={() => setFilterCat(null)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                !filterCat ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              All
            </button>
            {CATEGORIES.map(c => (
              <button
                key={c.value}
                onClick={() => setFilterCat(c.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                  filterCat === c.value ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        )}

        {/* Events Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : displayEvents.length === 0 ? (
          <div className="text-center py-20">
            <Calendar className="w-12 h-12 mx-auto text-muted-foreground/40 mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-1">
              {activeTab === 'my-events' ? 'No events yet' : 'No upcoming events'}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {activeTab === 'my-events'
                ? 'Schedule your first live event to get started'
                : 'Check back soon or schedule your own!'}
            </p>
            {user && (
              <Button onClick={() => setShowCreate(true)} variant="outline">
                <Plus className="w-4 h-4 mr-2" /> Schedule a Live
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayEvents.map(ev => (
              <EventCard key={ev.event_id} event={ev} user={user} onRsvp={handleRsvp} />
            ))}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateEventForm
          onClose={() => setShowCreate(false)}
          onCreated={() => fetchEvents()}
        />
      )}
    </div>
  );
}
